'use client'

import * as React from "react"
import { usePolicy } from "@/context/PolicyContext"
import {
  LayoutDashboard,
  NotebookText,
  TicketsPlane,
  Rss,
  TowerControl,
  Plane,
  User2,
  ShieldHalf,
  Tag,
  CalendarDays,
  Earth,
  ClipboardPaste,
  StickyNote,
  Medal,
  Settings,
  Map,
  TrendingUp,
} from "lucide-react"

import Link from "next/link"

import { NavMain } from "@/components/nav-main"
import { NavAdmin } from "@/components/nav-admin"
import { usePathname } from "next/navigation"
import { NavCommunity } from "@/components/nav-community"
import { NavDispatch } from "@/components/nav-dispatch"
import { NavUser } from "@/components/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  useSidebar,
  SidebarRail,
} from "@/components/ui/sidebar"

import { useAuth } from "@/context/AuthContext";
import Image from "next/image"

export function AppSidebar() {
  const { user } = useAuth();
  const { open } = useSidebar()
  const { hasPermissionToAccess } = usePolicy();
  const pathname = usePathname();
  const [streamerMode, setStreamerMode] = React.useState(user?.settings.streamer_mode);
  
  const data = {
    user: user,
    navMain: [
      {
        title: 'navigation.overview',
        url: `/${user?.username}`,
        icon: LayoutDashboard,
        isActive: pathname === `/${user?.username}`,
      },
      {
        title: 'navigation.flights',
        url: `/${user?.username}/flights`,
        icon: NotebookText,
        isActive: pathname.startsWith(`/${user?.username}/flight`),
      },
    ],
    dispatch: [
      {
        name: "navigation.route_planner",
        url: "/dispatch/route-planner",
        icon: ClipboardPaste,
        isActive: pathname.startsWith("/dispatch/route-planner") || pathname.startsWith("/dispatch/ofp/"),
      },
      {
        name: "navigation.contracts",
        url: "/dispatch/contracts",
        icon: TicketsPlane,
        isActive: pathname.startsWith("/dispatch/contracts"),
      },
      {
        name: "navigation.focus_airports",
        url: "/dispatch/focus-airports",
        icon: TowerControl,
        isActive: pathname.startsWith("/dispatch/focus-airports"),
      },
      {
        name: "navigation.tours",
        url: "/dispatch/tours",
        icon: Map,
        isActive: pathname.startsWith("/dispatch/tours"),
      },
    ],
    community: [
      {
        name: "navigation.activity",
        url: "/community/activity",
        icon: Rss,
        isActive: pathname === "/community/activity",
      },
      {
        name: "navigation.worldmap",
        url: "/community/world-map",
        icon: Earth,
        isActive: pathname === "/community/world-map",
      },
      {
        name: "navigation.leaderboard",
        url: "/community/leaderboard",
        icon: Medal,
        isActive: pathname === "/community/leaderboard",
      },
      {
        name: "navigation.events",
        url: "/community/events",
        icon: CalendarDays,
        isActive: pathname === "/community/events",
      }
    ],
    admin: [
      {
        name: "navigation.overview",
        url: '/admin',
        icon: LayoutDashboard,
        isActive: pathname === '/admin',
        permission: 'view-users',
      },
      {
        name: "navigation.statistics",
        url: '/admin/statistics',
        icon: TrendingUp,
        isActive: pathname === '/admin/statistics',
        permission: 'view-users',
      },
      {
        name: "navigation.users",
        url: '/admin/users',
        icon: User2,
        isActive: pathname.startsWith('/admin/users'),
        permission: 'view-users',
      },
      {
        name: "navigation.flights",
        url: '/admin/flights',
        icon: Plane,
        isActive: pathname.startsWith('/admin/flights'),
        permission: 'view-flights',
      },
      {
        name: "navigation.airports",
        url: '/admin/airports',
        icon: TowerControl,
        isActive: pathname.startsWith('/admin/airports'),
        permission: 'view-airports',
      },
      {
        name: "navigation.events",
        url: '/admin/events',
        icon: CalendarDays,
        isActive: pathname.startsWith('/admin/events'),
        permission: 'view-events',
      },
      {
        name: "navigation.tours",
        url: '/admin/tours',
        icon: Map,
        isActive: pathname.startsWith('/admin/tours'),
        permission: 'view-events',
      },
      {
        name: "Focus Airports",
        url: '/admin/focus-airports',
        icon: CalendarDays,
        isActive: pathname === '/admin/focus-airports',
        permission: 'view-focusweek',
      },
      {
        name: "navigation.docs",
        url: '/admin/docs',
        icon: StickyNote,
        isActive: pathname.startsWith('/admin/docs'),
        permission: 'view-pages',
      },
      {
        name: "navigation.ranks",
        url: '/admin/ranks',
        icon: ShieldHalf,
        isActive: pathname === '/admin/ranks',
        permission: 'view-ranks',
      },
      {
        name: "navigation.roles",
        url: '/admin/roles',
        icon: Tag,
        isActive: pathname === '/admin/roles',
        permission: 'view-roles',
      },
      {
        name: "navigation.settings",
        url: '/admin/settings',
        icon: Settings,
        isActive: pathname === '/admin/settings',
        permission: 'view-settings',
      }
    ]
  }

  React.useEffect(() => {
    setStreamerMode(user?.settings.streamer_mode);
  }, [user]);

  return (
    <Sidebar collapsible="icon">
      <div className="flex justify-center items-center p-2">
        <Link href="/" className="flex aspect-square size-10 items-center justify-center rounded-lg text-sidebar-primary-foreground">
          <Image src="/fd-logo-sidebar.png" alt="FlightDeck Logo" width={32} height={32} />
          <div className={"text-left leading-tight font-semibold ml-2 text-primary " + (open ? "block" : "hidden")}>
            FlightDeck
          </div>
        </Link>
      </div>
      <SidebarContent>
        <NavMain items={data.navMain} />
        <NavDispatch items={data.dispatch} />
        <NavCommunity items={data.community} />
        {
          hasPermissionToAccess() && !streamerMode && (
            <NavAdmin items={data.admin} />
          )
        }
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
